import React, { useEffect, useState, useRef  } from 'react';
import axios from 'axios';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ReactModal from 'react-modal';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Footerbar from './components/Footerbar';
import useAppLayout from './hooks/useAppLayout';
import API_URL from './api/config';

import imageCompression from 'browser-image-compression';
import { Link } from 'react-router-dom';

import ImageWithLoader from './components/ImageWithLoader';


// --- Global OrgChart script loader (only loads once for the application) ---
let orgChartScriptLoaded = false;
let orgChartLoadPromise = null;

const loadOrgChartScript = (src) => {
  if (orgChartScriptLoaded) {
    return Promise.resolve();
  }
  if (orgChartLoadPromise) {
    return orgChartLoadPromise;
  }

  orgChartLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => {
      orgChartScriptLoaded = true;
      resolve();
    };
    script.onerror = reject;
    document.body.appendChild(script);
  });

  return orgChartLoadPromise;
};
// --- End Global OrgChart script loader ---


function PrintTreePreview() {
  
  const { navigate, isSidebarVisible, showSidebar, toggleSidebar } = useAppLayout();
  const { menu, toggleMobileMenu } = useAppLayout();
  const chartContainerRef = useRef(null);
  const chartInstanceRef = useRef(null);

  const queryClient = useQueryClient();

  const getToken = () => localStorage.getItem('token');


 // Query to fetch family tree data
const { data: familyTreeData, isLoading: isLoadingFamilyTree, isError: isErrorFamilyTree } = useQuery({
    queryKey: ['familyTree'],
    queryFn: () =>
      axios
        .get(`${API_URL}/family-tree`, {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        })
        .then((res) => res.data),
    staleTime: 0,
    cacheTime: Infinity,
  });



   // Load OrgChart script when data is available
   useEffect(() => {
    if (!familyTreeData) return; // Wait for data to be available

    loadScript(`${process.env.PUBLIC_URL}/orgchart/orgchart.js`)
      .then(() => {
        initializeOrgChart(familyTreeData);
        //console.log("family TreeData:", familyTreeData);
      })
      .catch((error) => {
        console.error('Failed to load OrgChart script:', error);
      });

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }
    };
  }, [familyTreeData]); // Runs only when familyTreeData is updated


  const loadScript = (src) => {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = resolve;
      script.onerror = reject;
      document.body.appendChild(script);
    });
  };

  const chartBgColor = "#EBF4ED";

 
  const initializeOrgChart = (data) => {
    if (!window.OrgChart) return;
    const isMobile = window.innerWidth <= 768;
    const OrgChart = window.OrgChart;

    OrgChart.templates.family_template = Object.assign({}, OrgChart.templates.ana);
    OrgChart.templates.family_template.size = [86, 86];
    OrgChart.templates.family_template.plus = '';
    OrgChart.templates.family_template.minus = '';
    OrgChart.templates.family_template.rippleRadius = 40;
    OrgChart.templates.family_template.name = '<text style="font-size: 12px;" fill="#000000" x="43" y="100" text-anchor="middle">{val}</text>';
    OrgChart.templates.family_template.title = '<text style="font-size: 12px;" fill="#aeaeae" x="43" y="115" text-anchor="middle">{val}</text>';
    OrgChart.templates.family_template.img = '<clipPath id="{randId}"><circle cx="43" cy="43" r="38.5"></circle></clipPath></circle><image preserveAspectRatio="xMidYMid slice" clip-path="url(#{randId})" xlink:href="{val}" x="3" y="3" width="80" height="78"></image>';
    OrgChart.templates.family_template.node = '<circle stroke-width="3" fill="none" stroke="#FF1493" cx="43" cy="43" r="41.5"></circle>';


    OrgChart.templates.family_template_blue = Object.assign({}, OrgChart.templates.family_template);
    OrgChart.templates.family_template_blue.node = '<circle stroke-width="3" fill="none" stroke="#039BE5" cx="43" cy="43" r="41.5"></circle>';

    const chart = new OrgChart(chartContainerRef.current, {
      template: 'family_template',
      
      enableSearch: false,
      levelSeparation: isMobile ? 60 : 100,
      siblingSeparation: isMobile ? 60 : 100,
      scaleInitial: isMobile ? OrgChart.match.boundary : 1,
      scaleMin: 0.5,
      scaleMax: 3,
     
      nodeBinding: {
        name: 'name',
        title: 'title',
        img: 'img',
      },
      tags: {
        blue: { template: 'family_template_blue' },
        partner: { template: 'family_template' },
      },

      nodeMouseClick: OrgChart.action.none, // override default popup

    });

  

    chart.on('render-link', (sender, args) => {
      if (args.cnode.ppid !== undefined) {
        args.html += '<use xlink:href="#baby" x="' + args.p.xa + '" y="' + args.p.ya + '"/>';
      }
    });

    chart.on('redraw', function () {
      const chartElement = document.querySelector('#tree svg');
      if (chartElement) {
        chartElement.style.touchAction = 'pan-x pan-y';
      }
    });


    chart.load(data);
    chartInstanceRef.current = chart;


  };


  const { data: treeOrderDetails, isLoadingTb, isErrorTb } = useQuery({
    queryKey: ["treeOrderDetails"],
    queryFn: () =>
      axios
        .get(`${API_URL}/tree-order-details-api`, { withCredentials: true })
        .then((res) => res.data),
    
  });

 const [viewOrderDetailsModalOpen, setViewOrderDetailsModalOpen] = useState(false);

  
  const [loading, setLoading] = useState(false);

  const handleSendNotification = async () => {
    setLoading(true);
  
    try {
      const response = await axios.post(
        `${API_URL}/sending-notification-tree-order-api`,
        {}, // no body needed
        { withCredentials: true } //config goes here
      );
  
      toast.success(response.data.message || "Notification sent successfully, kindly wait" , { position: "top-right", autoClose: 6000 });
    } catch (error) {
      toast.error("Error sending details");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
 

  if (isLoadingFamilyTree) return( 
    <div className="loadingspinnerDiv">
  <div className="loadingspinner"></div>
</div>
     );
  
  if (isErrorFamilyTree) return <p>Error loading family tree data.</p>;


return (
  <div className="scrollPageContainer theme-light">
    <div className="main-wrapper">
      {isSidebarVisible && <Sidebar />}
      <Header toggleSidebar={toggleSidebar} />
      <Sidebar show={showSidebar} toggle={toggleSidebar} />
      <Footerbar toggleSidebar={toggleSidebar} />
      <ToastContainer />

      <div className="page-wrapper">
        <div className="content container-fluid">
          {/* Top bar */}
          <div className="d-flex align-items-center justify-content-between mb-3">
            <Link
              to="/print-tree-order"
              className="btn btn-light border d-inline-flex align-items-center gap-2 px-3 py-2"
              style={{ borderRadius: 10 }}
            >
              <i className="fa fa-arrow-left" style={{ color: "#184A2C", fontSize: 18 }} />
              <span className="fw-semibold">Back</span>
            </Link>

            <div className="text-end">
              <div className="fw-bold" style={{ fontSize: 16, color: "#184A2C" }}>
                Print Preview
              </div>
              <div className="text-muted" style={{ fontSize: 12 }}>
                Review your tree before completing the order
              </div>
            </div>
          </div>

          {/* Main preview card */}
          <div
            className="card border-0 shadow-sm"
            style={{ borderRadius: 14, overflow: "hidden" }}
          >
            {/* Card header */}
            <div
              className="d-flex align-items-center justify-content-between flex-wrap gap-2 px-3 py-3"
              style={{
                background:
                  "linear-gradient(135deg, rgba(24,74,44,0.10), rgba(117,180,55,0.06))",
                borderBottom: "1px solid rgba(0,0,0,0.06)",
              }}
            >
              <div>
                <div className="fw-bold" style={{ color: "#184A2C", fontSize: 15 }}>
                  Tree Preview Canvas
                </div>
                <div className="text-muted" style={{ fontSize: 12 }}>
                  Pinch/scroll to zoom and move the tree
                </div>
              </div>

              <div className="d-flex align-items-center gap-2">
                <button
                  onClick={() => setViewOrderDetailsModalOpen(true)}
                  className="btn btn-outline-success btn-sm px-3"
                  style={{ borderRadius: 10 }}
                >
                  <i className="fa-regular fa-file-lines me-2" />
                  Order Details
                </button>

                <button
                  onClick={handleSendNotification}
                  className="btn btn-success btn-sm px-3"
                  disabled={loading}
                  style={{
                    borderRadius: 10,
                    backgroundImage: "linear-gradient(135deg, #75B437, #184A2C)",
                    border: "none",
                    boxShadow: "0 8px 18px rgba(24,74,44,0.18)",
                  }}
                >
                  {loading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      Sending...
                    </>
                  ) : (
                    <>
                      <i className="fa-regular fa-bell me-2" />
                      Get Payment Notification
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Canvas area */}
            <div
              style={{
                position: "relative",
                width: "100%",
                height: "620px",
                backgroundColor: treeOrderDetails?.background_color || "#ffffff",
              }}
            >
              {/* Header overlay */}
              {treeOrderDetails?.header && (
                <div
                  style={{
                    position: "absolute",
                    top: 12,
                    left: "50%",
                    transform: "translateX(-50%)",
                    zIndex: 20,
                    maxWidth: "92%",
                  }}
                >
                  <div
                    className="px-3 py-2 fw-bold text-center"
                    style={{
                      background: "rgba(255,255,255,0.78)",
                      border: "1px solid rgba(0,0,0,0.08)",
                      backdropFilter: "blur(6px)",
                      borderRadius: 10,
                      color: "#184A2C",
                      boxShadow: "0 10px 22px rgba(0,0,0,0.10)",
                    }}
                  >
                    {treeOrderDetails.header}
                  </div>
                </div>
              )}

              {/* OrgChart container */}
              <div id="tree" ref={chartContainerRef} style={{ width: "100%", height: "100%" }} />

              {/* Bottom helper strip */}
              <div
                className="d-flex align-items-center justify-content-between flex-wrap gap-2 px-3 py-2"
                style={{
                  position: "absolute",
                  left: 12,
                  right: 12,
                  bottom: 12,
                  zIndex: 20,
                  background: "rgba(255,255,255,0.72)",
                  border: "1px solid rgba(0,0,0,0.08)",
                  backdropFilter: "blur(6px)",
                  borderRadius: 12,
                }}
              >
                <div className="text-muted" style={{ fontSize: 12 }}>
                  <i className="fa-solid fa-hand-pointer me-2" />
                  Drag to move • Scroll/Pinch to zoom
                </div>

                <div className="d-flex gap-2">
                  <button
                    onClick={() => setViewOrderDetailsModalOpen(true)}
                    className="btn btn-light btn-sm border"
                    style={{ borderRadius: 10 }}
                  >
                    View Details
                  </button>

                  <button
                    onClick={handleSendNotification}
                    className="btn btn-success btn-sm"
                    disabled={loading}
                    style={{
                      borderRadius: 10,
                      backgroundImage: "linear-gradient(135deg, #75B437, #184A2C)",
                      border: "none",
                    }}
                  >
                    {loading ? "Sending..." : "Notify Me"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Order details modal */}
          <ReactModal
            isOpen={viewOrderDetailsModalOpen}
            onRequestClose={() => setViewOrderDetailsModalOpen(false)}
            className="Modal"
            overlayClassName="Overlay"
          >
            <div className="modal-header">
              <h5 className="modal-title">Tree Order Details</h5>
              <button
                type="button"
                className="btn-close modalCloseButton"
                onClick={() => setViewOrderDetailsModalOpen(false)}
              />
            </div>

            <div className="modal-body">
              {isLoadingTb && <p>Loading order details...</p>}
              {isErrorTb && <p className="text-danger">Error loading order details</p>}

              {treeOrderDetails ? (
                <div className="row">
                  <h6 className="fw-bold mb-2">{treeOrderDetails.header}</h6>

                  <div className="text-muted" style={{ fontSize: 13, marginBottom: 10 }}>
                    Confirm everything below is correct before completing payment.
                  </div>

                  <div className="col-12">
                    <div
                      className="p-3"
                      style={{
                        borderRadius: 12,
                        background: "#F7FFF8",
                        border: "1px solid #DFF7E8",
                      }}
                    >
                      <p className="mb-1"><strong>Tree Size:</strong> {treeOrderDetails.tree_size_name}</p>
                      <p className="mb-1"><strong>No. of Copies:</strong> {treeOrderDetails.number_of_copies}</p>
                      <p className="mb-1">
                        <strong>Background:</strong>{" "}
                        <span
                          style={{
                            display: "inline-block",
                            width: 18,
                            height: 18,
                            backgroundColor: treeOrderDetails.background_color,
                            border: "1px solid rgba(0,0,0,0.2)",
                            borderRadius: 4,
                            marginLeft: 6,
                            verticalAlign: "middle",
                          }}
                        />
                      </p>
                      <p className="mb-1"><strong>Names:</strong> {treeOrderDetails.full_names}</p>
                      <p className="mb-1"><strong>Phone:</strong> {treeOrderDetails.phone_number}</p>
                      <p className="mb-1"><strong>Email:</strong> {treeOrderDetails.email}</p>
                      <p className="mb-1"><strong>Pickup Location:</strong> {treeOrderDetails.pickup_location_name}</p>
                      <p className="mb-1"><strong>Total Amount:</strong> KES {treeOrderDetails.total_amount}</p>
                      <p className="mb-0">
                        <strong>Date:</strong> {new Date(treeOrderDetails.date_time).toLocaleString()}
                      </p>
                    </div>

                    <div className="text-muted mt-3" style={{ fontSize: 13 }}>
                      Place your tree order and complete it through the notification sent to you.
                    </div>
                  </div>
                </div>
              ) : (
                <p>No active tree order found.</p>
              )}
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-light border"
                onClick={() => setViewOrderDetailsModalOpen(false)}
              >
                Close
              </button>
            </div>
          </ReactModal>

          <div className="row end-page-space" />
        </div>
      </div>
    </div>
  </div>
);

  
}

export default PrintTreePreview;
